import classNames from 'classnames'
import React, { useCallback, useRef, useState } from 'react'

import {
  AbsolutePopup,
  IPopupMethods,
  PopPaper,
  TPopupHorizontal,
  TPopupVertical,
} from 'core-components/library/popup'

import { PillButton } from './PillButton'

import styles from './OverflowMenu.module.scss'

interface IProps {
  label: React.ReactNode
  children: React.ReactNode
  /** Which edge of the trigger the panel lines up with */
  align?: 'left' | 'right'
  size?: 'sm' | 'md'
  title?: string
}

/**
 * Single trigger that hides a set of buttons behind one control, opening them as
 * a vertical list. Used where a row of buttons would outgrow the space it has:
 * the request action bar's low-frequency utilities, and action rows whose
 * transitions are too many or too wordy to sit inline.
 *
 * Built on AbsolutePopup + PopPaper so it carries the same surface, shadow and
 * flip-when-clipped behaviour as every other menu in the app, rather than a
 * look-alike of its own.
 *
 * Items are not closed on click: some of them open a dialog that renders inside
 * this panel, and unmounting the panel would take the dialog with it. Such items
 * mark themselves with `data-keep-menu-open`; everything else closes the menu.
 */
export const OverflowMenu = ({ label, children, align = 'left', size, title }: IProps) => {
  const popupRef = useRef<IPopupMethods>(null)
  const [isOpen, setIsOpen] = useState(false)

  const handleVisibilityChange = useCallback((visible: boolean) => {
    setIsOpen(visible)
    return visible
  }, [])

  return (
    <AbsolutePopup
      ref={popupRef}
      rootClassName={styles.root}
      verticalStartPosition={TPopupVertical.Bottom}
      horizontalStartPosition={align === 'right' ? TPopupHorizontal.Right : TPopupHorizontal.Left}
      horizontalDirection={align === 'right' ? TPopupHorizontal.Left : TPopupHorizontal.Right}
      visibilityCallback={handleVisibilityChange}
      popupContent={(popupData) => (
        <PopPaper className={styles.paper}>
          <div
            className={styles.list}
            role="menu"
            onClick={(e) => {
              // The panel is portalled, but React events still bubble along the
              // component tree — without this the row underneath would toggle.
              e.stopPropagation()
              if ((e.target as HTMLElement).closest('[data-keep-menu-open]')) return
              popupData.action.close()
            }}
          >
            {children}
          </div>
        </PopPaper>
      )}
    >
      <PillButton
        variant="outlined"
        size={size}
        // AbsolutePopup opens on mousedown on the trigger and never toggles, and
        // its own outside-click handler treats the trigger as "inside". So the
        // close half is ours: intercept in the capture phase before the library's
        // handler runs, and stop the event so it does not reopen straight after.
        onMouseDownCapture={(e) => {
          if (isOpen) {
            e.stopPropagation()
            popupRef.current?.close()
          }
        }}
        onClick={(e) => {
          // The trigger sits inside a clickable row — using the menu must not
          // also expand the card.
          e.stopPropagation()
        }}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        title={title}
      >
        {label}
        <i
          className={classNames(
            'kbase',
            isOpen ? 'kbase-omni-chevron-up' : 'kbase-omni-chevron-down',
            styles.chevron
          )}
          aria-hidden
        />
      </PillButton>
    </AbsolutePopup>
  )
}
